/**
 * Hook for managing redaction reasons.
 * Provides CRUD operations and caching for reasons.
 */

import { useState, useEffect, useCallback } from 'react';
import { reasonsApi, apiHelpers } from '../lib/api';
import type { RedactionReason } from '../types';

interface UseReasonsReturn {
  reasons: RedactionReason[];
  isLoading: boolean;
  error: string | null;
  createReason: (title: string, description?: string) => Promise<RedactionReason | null>;
  updateReason: (id: string, title: string, description?: string) => Promise<RedactionReason | null>;
  deleteReason: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useReasons(): UseReasonsReturn {
  const [reasons, setReasons] = useState<RedactionReason[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all reasons
  const fetchReasons = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await reasonsApi.getReasons({ limit: 1000 });
      setReasons(response.reasons);
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to fetch reasons:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  // Create a new reason
  const createReason = useCallback(async (title: string, description?: string): Promise<RedactionReason | null> => {
    try {
      const newReason = await reasonsApi.createReason({ title, description });
      setReasons(prev => [newReason, ...prev]);
      return newReason;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to create reason:', errorMessage);
      return null;
    }
  }, []);

  // Update an existing reason
  const updateReason = useCallback(async (id: string, title: string, description?: string): Promise<RedactionReason | null> => {
    try {
      const updatedReason = await reasonsApi.updateReason(id, { title, description });
      setReasons(prev =>
        prev.map(r => r.id === id ? updatedReason : r)
      );
      return updatedReason;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to update reason:', errorMessage);
      return null;
    }
  }, []);

  // Delete a reason
  const deleteReason = useCallback(async (id: string): Promise<boolean> => {
    try {
      await reasonsApi.deleteReason(id);
      setReasons(prev => prev.filter(r => r.id !== id));
      return true;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to delete reason:', errorMessage);
      return false;
    }
  }, []);

  // Refresh reasons
  const refresh = useCallback(async () => {
    await fetchReasons();
  }, [fetchReasons]);

  return {
    reasons,
    isLoading,
    error,
    createReason,
    updateReason,
    deleteReason,
    refresh,
  };
}
